import React from "react";

// Custom SVG icons that read as their real-world objects at 20-24px sizes.
// Each icon accepts standard SVG props (className, strokeWidth, style, etc.).

const wrap = (children, props = {}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={props.strokeWidth ?? 1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
);

// Soccer ball: circle + centred pentagon + 5 short seams reaching to the edge.
export const BallIcon = (props) =>
  wrap(
    (() => {
      const r = 10;
      const rp = 3.2;
      const start = -Math.PI / 2;
      const verts = Array.from({ length: 5 }, (_, i) => {
        const a = start + (i * 2 * Math.PI) / 5;
        return { x: Math.cos(a) * rp, y: Math.sin(a) * rp };
      });
      const pentPath = verts.map((p, i) => `${i === 0 ? "M" : "L"} ${(12 + p.x).toFixed(2)} ${(12 + p.y).toFixed(2)}`).join(" ") + " Z";
      return (
        <g>
          <circle cx={12} cy={12} r={r} />
          <path d={pentPath} fill="currentColor" stroke="none" />
          {verts.map((p, i) => {
            const mag = Math.hypot(p.x, p.y) || 1;
            const ex = 12 + (p.x / mag) * (r - 0.5);
            const ey = 12 + (p.y / mag) * (r - 0.5);
            return <line key={i} x1={12 + p.x} y1={12 + p.y} x2={ex} y2={ey} />;
          })}
        </g>
      );
    })(),
    props
  );

// Soccer goal: crossbar + posts + net (diagonal + horizontal weave lines).
export const GoalIcon = (props) =>
  wrap(
    <g>
      {/* Frame */}
      <path d="M 3 6 L 3 20" />
      <path d="M 21 6 L 21 20" />
      <path d="M 3 6 L 21 6" />
      {/* Net weave — vertical strands */}
      <path d="M 7 6 L 7 18" strokeOpacity="0.55" />
      <path d="M 12 6 L 12 19" strokeOpacity="0.55" />
      <path d="M 17 6 L 17 18" strokeOpacity="0.55" />
      {/* Net weave — horizontal strands */}
      <path d="M 3 10 L 21 10" strokeOpacity="0.55" />
      <path d="M 3 14 L 21 14" strokeOpacity="0.55" />
      <path d="M 3.5 18 L 20.5 18" strokeOpacity="0.55" />
      {/* Ground line */}
      <path d="M 2 20 L 22 20" />
    </g>,
    props
  );

// KwikGoal-style mannequin: head + body + pole + wide base.
export const MannequinIcon = (props) =>
  wrap(
    <g>
      <circle cx={12} cy={5.5} r={2} />
      <path d="M 9 11 Q 12 8 15 11 L 15 15 Q 12 16 9 15 Z" />
      <path d="M 11.4 15 L 11.4 20" />
      <path d="M 12.6 15 L 12.6 20" />
      <ellipse cx={12} cy={20.5} rx={5.5} ry={1.2} />
    </g>,
    props
  );

// Mini goal: exactly the same visual grammar as GoalIcon but narrower.
// Posts pulled inwards from x=3/21 → x=7/17 so the reader instantly
// distinguishes a Goal from a Mini Goal in the toolbar.
export const MiniGoalIcon = (props) =>
  wrap(
    <g>
      {/* Frame */}
      <path d="M 7 8 L 7 20" />
      <path d="M 17 8 L 17 20" />
      <path d="M 7 8 L 17 8" />
      {/* Net weave — vertical strands */}
      <path d="M 10 8 L 10 19" strokeOpacity="0.55" />
      <path d="M 14 8 L 14 19" strokeOpacity="0.55" />
      {/* Net weave — horizontal strands */}
      <path d="M 7 12 L 17 12" strokeOpacity="0.55" />
      <path d="M 7 16 L 17 16" strokeOpacity="0.55" />
      {/* Ground line — full width so the icon still reads as a goal on a pitch */}
      <path d="M 4 20 L 20 20" />
    </g>,
    props
  );

// Coach ADD-pane icon: a simple capital "C" so the tool reads at a glance
// alongside the other player-like icons. The text inherits `currentColor`
// via `fill="currentColor"` so it picks up the same colour as every other
// icon in the toolbar (active / hover / disabled states all work).
export const CoachLetterIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    {...props}
  >
    <text
      x="12"
      y="17"
      textAnchor="middle"
      fontSize="16"
      fontWeight="800"
      fontFamily="Manrope, sans-serif"
    >
      C
    </text>
  </svg>
);

